/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, ReactNode} from 'react';
import Grid from '@material-ui/core/Grid';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import TabView, {TabPropTypes} from '@ion/dls/src/components/tabs/TabView';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor, dlsTypography} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {CTALink} from '../buttons/CTALink';

export interface FormSectionType {
  children: ReactNode;
  title?: string | ReactNode;
  description?: string | ReactNode;
  legend?: string | ReactNode;
  legendAsError?: boolean;
  bottomBorder?: boolean;
  gridSpacing?: any;
  required?: boolean;
  bottomStyle?: boolean;
  stickyTitle?: boolean;
  overrideClasses?: GenericObject;
  ctaText?: string;
  subtitle?: string;
  tabProps?: React.ComponentProps<typeof TabPropTypes>;
  onCTAClick?: MouseEventHandler<HTMLSpanElement>;
}

const useStyles = makeStyles(theme => ({
  formSection : {
    borderBottom : ({bottomBorder, isCurrentTheme}: {bottomBorder: boolean, isCurrentTheme: boolean}) =>
      (!isCurrentTheme && bottomBorder) ? `solid 1px ${dlsColor('grey4')}` : 'none',
    padding : toggleThemeStyle('36px 0', '0px')
  },

  formSectionContent : {
    display        : 'flex',
    justifyContent : 'space-between',
    gap            : toggleThemeStyle(0, 8),
    alignItems     : 'center'
  },

  formTitleSection : {
    display       : 'flex',
    flexDirection : 'column'
  },

  formSectionTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : toggleThemeStyle('uppercase', 'capitalize'),
    color         : toggleThemeStyle('inherit', dlsColor('grey2')),
    fontSize      : toggleThemeStyle('inherit', '20px'),
    fontFamily    : toggleThemeStyle('inherit', 'Roboto')
  },

  description : {
    ...dlsAlt('typography', 'label2'),
    fontSize      : '14px',
    marginBottom  : '20px',
    marginTop     : '-8px',
    lineHeight    : '20px',
    letterSpacing : '0px',
    fontFamily    : toggleThemeStyle('Neo Sans Std', 'Inter')
  },

  legend : {
    ...dlsAlt('typography', 'label2'),
    letterSpacing : toggleThemeStyle('0px', '1px'),
    color         : ({legendAsError, isCurrentTheme}: {legendAsError: boolean, isCurrentTheme: boolean}) => (
      legendAsError ? dlsColor('caRed') : dlsColor(isCurrentTheme ? 'red' : 'aaBlue')
    ),
    position    : 'relative',
    bottom      : ({bottomStyle}: {bottomStyle: boolean}) => bottomStyle ? '35px' : 0,
    fontFamily  : toggleThemeStyle('Roboto', 'Inter'),
    fontWeight  : toggleThemeStyle('normal', '600'),
    // the current theme's "* Required Field" note sits right after the
    // title, any cta still at the row's far end ...
    order       : toggleThemeStyle(0, 1),
    marginRight : toggleThemeStyle(0, 'auto')
  },

  asterisk : {
    color  : toggleThemeStyle(dlsColor('aaBlue'), dlsColor('red')),
    margin : '0 2px'
  },

  stickyFormSectionContent : {
    display         : 'flex',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    position        : 'sticky',
    top             : 0,
    backgroundColor : dlsColor('grey6'),
    zIndex          : 99999
  },

  ctaLink : {
    ...dlsTypography('cta'),
    order          : toggleThemeStyle(0, 2),
    color          : dlsColor('aaBlue'),
    cursor         : 'pointer',
    letterSpacing  : '1px',
    lineHeight     : 'inherit',
    fontSize       : 'inherit',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },

  infoLabel : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey3'),
    letterSpacing : 'normal'
  }
}));

/**
 * @FormSection
 * Wrapper component for form sections
 *
 * @param {Node} children Component that is to be wrapped
 * @param {Node | string} title Section title
 * @param {Node | string} legend Section legend (optional)
 * @param {boolean} bottomBorder should the section have a border
 *   to separate it between sections (optional)
 *
 * ```typescript
 *
 * import {FormSection} from '@ion/dls/src/components/forms/FormSection';
 *
 * <FormSection bottomBorder
 *   title='Awesome Section'
 *   legend='*Required Fields'
 *   >
 *   <Component />
 * </FormSection>
 *
 * ```
 *
 */
export const FormSection = ({
  children,
  title,
  description,
  ctaText,
  onCTAClick,
  overrideClasses,
  legend = null,
  bottomBorder = false,
  legendAsError = false,
  bottomStyle = false,
  gridSpacing = 4,
  required = false,
  stickyTitle = false,
  subtitle,
  tabProps
}: FormSectionType) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();

  const classes = useStyles({
    isCurrentTheme, bottomBorder, legendAsError, bottomStyle
  });

  const header = (
    <div className={stickyTitle
      ? classnames(classes.stickyFormSectionContent, overrideClasses?.formSectionContent)
      : classnames(classes.formSectionContent, overrideClasses?.formSectionContent)}>
      {title && (
        <h1 className={classes.formSectionTitle}>
          {title}
          {required && <span className={classes.asterisk}>*</span>}
        </h1>
      )}
      {ctaText && (
        <CTALink className={classes.ctaLink}
          onClick={onCTAClick}>
          {ctaText}
        </ CTALink>
      )}
      {legend && (
        <div className={classes.legend}>
          {legend}
        </div>
      )}
    </div>
  );

  return (
    <section className={classnames(classes.formSection, overrideClasses?.formSection)}>
      {!description ? (
        header
      ) : (
        <div className={classes.formTitleSection}>
          {header}
          <div className={classes.description}>
            {description}
          </div>
        </div>
      )}
      {subtitle && (
        <span className={classes.infoLabel}>
          {subtitle}
        </span>
      )}
      {tabProps && <TabView {...tabProps} />}
      <Grid container
        justifyContent='space-between'
        spacing={gridSpacing}>
        {children}
      </Grid>
    </section>
  );
};

export default FormSection;
